import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { TherapistAvatar } from 'components/ui/therapist-avatar';
import { IntakeProfile } from '../../api/intake';

interface IntakeProfileRevealProps {
  profile: IntakeProfile;
  onBook: (therapist: any) => void;
  onComplete: () => void;
}


export const IntakeProfileReveal: React.FC<IntakeProfileRevealProps> = ({
  profile,
  onBook,
  onComplete,
}) => {
  const { t } = useTranslation();
  const ai = profile.aiProfile;
  const matches = profile.matches || [];
  const therapists = profile.therapists || [];

  if (!ai) return null;

  // Find the top match therapist object
  const topMatch = matches[0];
  const topTherapist = topMatch
    ? therapists.find(
        (th: any) => th.userId === topMatch.therapistId || th.id === topMatch.therapistId
      )
    : null;

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-white to-cyan-50 px-4 py-8"
      data-testid="intake-reveal"
    >
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2 opacity-0 animate-fade-in-up">
          <h1 className="text-2xl font-bold text-gray-800">
            {t('intake.revealTitle')}
          </h1>
          <p className="text-gray-500">{t('intake.revealSubtitle')}</p>
        </div>

        {/* Key insight — the hero "this platform gets me" moment */}
        <div className="bg-gradient-to-br from-[#00c4cc] to-[#00a3ab] rounded-2xl p-6 text-white opacity-0 animate-stagger-1">
          <p className="text-lg leading-relaxed font-medium">
            {ai.keyInsight}
          </p>
        </div>

        {/* Stressor themes */}
        <div className="bg-white rounded-2xl p-5 shadow-sm opacity-0 animate-stagger-2">
          <h3 className="font-semibold text-gray-800 mb-3">
            {t('intake.revealThemes')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {ai.stressorThemes.map((theme, i) => (
              <span
                key={i}
                className="px-3 py-1.5 bg-cyan-50 text-[#00c4cc] rounded-full text-sm font-medium"
                data-testid="stressor-theme"
              >
                {theme}
              </span>
            ))}
          </div>
        </div>

        {/* Cultural context */}
        <div className="bg-white rounded-2xl p-5 shadow-sm opacity-0 animate-stagger-3">
          <h3 className="font-semibold text-gray-800 mb-2">
            {t('intake.revealContext')}
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            {ai.culturalContext}
          </p>
        </div>

        {/* Therapist match */}
        {topMatch && topTherapist && (
          <div className="space-y-4 opacity-0 animate-stagger-5">
            <h3 className="font-semibold text-gray-800 text-center text-lg">
              {t('intake.revealRecommendation')}
            </h3>

            <div
              className="bg-white rounded-2xl p-5 shadow-sm border-2 border-[#00c4cc]/20"
              data-testid="therapist-match-card"
            >
              <div className="flex items-start gap-4">
                <TherapistAvatar imageUrl={topTherapist?.imageUrl} name={topTherapist?.name} size="md" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-800">
                    {topTherapist?.name ||
                      (topTherapist?.firstName || topTherapist?.user?.firstName
                        ? `Dr. ${topTherapist.firstName || topTherapist.user?.firstName} ${topTherapist.lastName || topTherapist.user?.lastName || ''}`.trim()
                        : t('intake.revealTherapist'))}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-[#00c4cc] font-semibold">
                      {Math.round(topMatch.score * 100)}% {t('intake.revealMatch')}
                    </span>
                  </div>
                  {topTherapist?.specialties && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {topTherapist.specialties.slice(0, 3).map((s: any) => (
                        <span
                          key={s.id || s.name}
                          className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                        >
                          {s.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                {topMatch.explanation}
              </p>
              <Button
                onClick={() => onBook(topTherapist)}
                className="w-full mt-4 text-white"
                style={{ backgroundColor: '#00c4cc' }}
                data-testid="intake-book-cta"
              >
                {t('intake.revealBook')}
              </Button>
            </div>

            {/* Other matches */}
            {matches.length > 1 && (
              <div className="space-y-3">
                <p className="text-center text-gray-500 text-sm">
                  {t('intake.revealOthers')}
                </p>
                {matches.slice(1).map((match) => {
                  const th = therapists.find(
                    (th: any) => th.userId === match.therapistId || th.id === match.therapistId
                  );
                  return (
                    <div
                      key={match.therapistId}
                      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <TherapistAvatar imageUrl={th?.imageUrl} name={th?.name} size="xs" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 text-sm">
                            {th?.name ||
                              (th?.firstName || th?.user?.firstName
                                ? `Dr. ${th.firstName || th.user?.firstName}`
                                : `Therapist #${match.rank}`)}
                          </p>
                          <p className="text-xs text-[#00c4cc]">
                            {Math.round(match.score * 100)}% {t('intake.revealMatch')}
                          </p>
                        </div>
                      </div>
                      <p className="mt-2 text-gray-500 text-xs">{match.explanation}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Educational content */}
        <div className="space-y-4 opacity-0 animate-stagger-6">
          <h3 className="font-semibold text-gray-800 text-center text-lg">
            {t('intake.eduTitle')}
          </h3>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-purple-100">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💜</span>
              <div>
                <h4 className="font-semibold text-gray-800 text-sm">{t('intake.eduWhyTitle')}</h4>
                <p className="text-gray-600 text-sm mt-1">{t('intake.eduWhyText')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🤝</span>
              <div>
                <h4 className="font-semibold text-gray-800 text-sm">{t('intake.eduPrepareTitle')}</h4>
                <p className="text-gray-600 text-sm mt-1">{t('intake.eduPrepareText')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-green-100">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🌍</span>
              <div>
                <h4 className="font-semibold text-gray-800 text-sm">{t('intake.eduNotAloneTitle')}</h4>
                <p className="text-gray-600 text-sm mt-1">{t('intake.eduNotAloneText')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Complete profile CTA */}
        <div className="pt-4 opacity-0 animate-stagger-6" data-testid="intake-complete-profile">
          <p className="text-center text-gray-500 text-sm mb-3">
            {t('intake.revealCompleteHint')}
          </p>
          <Button
            onClick={onComplete}
            variant="outline"
            className="w-full border-[#00c4cc] text-[#00c4cc] hover:bg-[#00c4cc]/10"
            data-testid="intake-complete-btn"
          >
            {t('intake.revealCompleteBtn')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IntakeProfileReveal;
